import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useLanguage } from '../hooks/useLanguage';
// Custom Arrow SVG component
const ArrowIcon = ({
  direction
}: {
  direction: 'ltr' | 'rtl';
}) => <img src="https://cdn.magicpatterns.com/uploads/izZ9BGqUrPw16fCLMTS9E1/arrow.svg" alt="" className={`w-6 h-auto ${direction === 'rtl' ? 'rotate-180' : ''}`} data-id="element-330" />;
type CategoryKey = 'all' | 'cardAssociations' | 'systemsCompliance' | 'softwareHardware' | 'dataCentres';
interface Partner {
  id: string;
  name: string;
  logo: string;
  descriptionKey?: string;
  description?: string;
  category: CategoryKey;
}
const partners: Partner[] = [{
  id: 'visa',
  name: 'Visa',
  logo: 'https://cdn.magicpatterns.com/uploads/uuPGiwz8ac4NxLex2682ty/visa.png',
  category: 'cardAssociations'
}, {
  id: 'mastercard',
  name: 'Mastercard',
  logo: 'https://cdn.magicpatterns.com/uploads/dZtRuyWKjZNGGXqqtdWwcM/master.png',
  category: 'cardAssociations'
}, {
  id: 'amex',
  name: 'American Express',
  logo: 'https://cdn.magicpatterns.com/uploads/23vnYXcXXu8EpU788bEnWz/amex.png',
  category: 'cardAssociations'
}, {
  id: 'unionpay',
  name: 'UnionPay',
  logo: 'https://cdn.magicpatterns.com/uploads/1s79DxYC7ApNpmpZ1hDeLn/unionpay.png',
  category: 'cardAssociations'
}, {
  id: 'affn',
  name: 'AFFN',
  logo: 'https://cdn.magicpatterns.com/uploads/rvcGEx1ZEeRdvfZJYgTTEg/affn.png',
  category: 'cardAssociations'
}];
const categoryKeys: CategoryKey[] = ['all', 'cardAssociations', 'systemsCompliance', 'softwareHardware', 'dataCentres'];
export function PartnersGrid() {
  const [activeCategory, setActiveCategory] = useState<CategoryKey>('all');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const {
    ref,
    isVisible
  } = useScrollReveal();
  const {
    t,
    direction
  } = useLanguage();
  const categoryLabels: Record<CategoryKey, string> = {
    all: t('partnersPage.grid.filterAll'),
    cardAssociations: t('partnersPage.grid.filterCardAssociations'),
    systemsCompliance: t('partnersPage.grid.filterSystemsCompliance'),
    softwareHardware: t('partnersPage.grid.filterSoftwareHardware'),
    dataCentres: t('partnersPage.grid.filterDataCentres')
  };
  const filteredPartners = activeCategory === 'all' ? partners : partners.filter(p => p.category === activeCategory);
  return <section ref={ref} className="py-20 bg-white relative z-10" aria-label="Partners List" data-id="element-331">
      <div className="container mx-auto px-6 md:px-12" data-id="element-332">
        <div className="flex flex-col lg:flex-row gap-12" data-id="element-333">
          {/* Desktop Filters - Left Sidebar */}
          <div className="hidden lg:block w-72 flex-shrink-0" data-id="element-334">
            <div className="sticky top-24 space-y-2" data-id="element-335">
              {categoryKeys.map((key, index) => <button key={key} onClick={() => setActiveCategory(key)} className={`w-full py-4 px-2 flex items-center justify-between group transition-all duration-300 border-b border-gray-100 ${direction === 'rtl' ? 'text-right' : 'text-left'} ${activeCategory === key ? 'text-[#061D76] font-bold' : 'text-gray-400 hover:text-[#061D76]'}`} data-id="element-336">
                  <span className="flex items-center gap-4 min-w-0" data-id="element-337">
                    <span className={`text-sm font-medium flex-shrink-0 ${activeCategory === key ? 'text-[#25B8D6]' : 'text-gray-300'}`} data-id="element-338">
                      {index + 1}
                    </span>
                    <span className="text-lg" data-id="element-339">{categoryLabels[key]}</span>
                  </span>

                  {/* Arrow Icon - Visible only for active item */}
                  <span className={`transition-opacity duration-300 ${activeCategory === key ? 'opacity-100' : 'opacity-0'}`} data-id="element-340">
                    <ArrowIcon direction={direction} data-id="element-341" />
                  </span>
                </button>)}
            </div>
          </div>

          {/* Mobile Filter - Dropdown */}
          <div className="lg:hidden relative z-20" data-id="element-342">
            <button onClick={() => setIsDropdownOpen(!isDropdownOpen)} className="w-full bg-white border border-gray-200 rounded-lg px-6 py-4 flex items-center justify-between text-[#061D76] font-bold text-lg shadow-sm" data-id="element-343">
              <span data-id="element-344">{categoryLabels[activeCategory]}</span>
              <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`} data-id="element-345" />
            </button>

            {isDropdownOpen && <div className="absolute top-full left-0 right-0 w-full mt-2 bg-white border border-gray-100 rounded-lg shadow-xl overflow-hidden" data-id="element-346">
                {categoryKeys.map(key => <button key={key} onClick={() => {
              setActiveCategory(key);
              setIsDropdownOpen(false);
            }} className={`w-full px-6 py-3 hover:bg-gray-50 transition-colors ${direction === 'rtl' ? 'text-right' : 'text-left'} ${activeCategory === key ? 'text-[#25B8D6] font-bold' : 'text-[#061D76]'}`} data-id="element-347">
                    {categoryLabels[key]}
                  </button>)}
              </div>}
          </div>

          {/* Partners Grid */}
          <div className="flex-1" style={{
          minHeight: '600px'
        }} data-id="element-348">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-349">
              {filteredPartners.map((partner, index) => <div key={partner.id} className={`bg-white border border-gray-100 p-8 hover:shadow-lg transition-all duration-300 group reveal-hidden ${isVisible ? 'animate-fade-up' : ''}`} style={{
              animationDelay: `${index * 100}ms`
            }} data-id="element-350">
                  {/* Logo Container - original size, no constraints */}
                  <div className={`mb-8 flex items-center ${direction === 'rtl' ? 'justify-end' : 'justify-start'}`} data-id="element-351">
                    <img src={partner.logo} alt={partner.name} className="object-contain" style={{
                  imageRendering: 'auto'
                }} data-id="element-352" />
                  </div>

                  {/* Description */}
                  <p className={`text-[#061D76]/70 text-sm leading-relaxed ${direction === 'rtl' ? 'text-right' : 'text-left'}`} data-id="element-353">
                    {t('partnersPage.grid.partnerDescription')}
                  </p>
                </div>)}
            </div>

            {/* Empty State */}
            {filteredPartners.length === 0 && <div className="py-20 text-center text-gray-400" data-id="element-354">
                <p data-id="element-355">{t('partnersPage.grid.noPartners')}</p>
              </div>}
          </div>
        </div>
      </div>
    </section>;
}